// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Card component module for visualizing customer asset functional status data.
 * Displays a stacked bar chart showing functional vs non-functional assets per customer.
 */
import { BarChartCard } from '@/components/bar-chart-card';
import { ASSET_FUNCTIONAL_STATUS_LABELS } from '@/constants/asset-functional-status';
import { CustomerFunctionalStatusReport } from '@/customer/api/get-customer-report';
import {
  CustomerReportSort,
  useCustomerReport,
} from '@/customer/hooks/use-customer-report';
import { useTheme } from '@mui/material';

/**
 * Type definition for the keys we want to extract from CustomerFunctionalStatusReport.
 * Specifically targets the count fields for functional and non-functional assets.
 */
type Key = Extract<
  keyof CustomerFunctionalStatusReport,
  'functionalCount' | 'needsReviewCount' | 'nonFunctionalCount'
>;

/**
 * Array of keys to use for the bar chart data series.
 * Order determines the stacking order in the visualization.
 */
const KEYS: Key[] = [
  'functionalCount',
  'needsReviewCount',
  'nonFunctionalCount',
];

/**
 * Extracts the customer name from a report datum for labeling.
 * Used to label each bar in the chart with the corresponding customer name.
 *
 * @param datum - The customer functional status report entry
 * @returns The customer's name as a string
 */
function getLabelForDatum(datum: CustomerFunctionalStatusReport) {
  return datum.customer.name;
}

/**
 * Maps data keys to their display labels using predefined status labels.
 * Converts technical keys to user-friendly display text.
 *
 * @param key - The data key to get a label for
 * @returns User-friendly label for the given key
 */
function getLabelForKey(key: Key) {
  switch (key) {
    case 'functionalCount':
      return ASSET_FUNCTIONAL_STATUS_LABELS['FUNCTIONAL'];

    case 'needsReviewCount':
      return ASSET_FUNCTIONAL_STATUS_LABELS['NEEDS_REVIEW'];

    case 'nonFunctionalCount':
      return ASSET_FUNCTIONAL_STATUS_LABELS['NON_FUNCTIONAL'];
  }
}

/**
 * Props for the CustomerFunctionalStatusReportCard component
 */
interface Props {
  /**
   * Sort configuration for the customer report data.
   * Determines the order of customers in the visualization.
   */
  sort: CustomerReportSort;

  /**
   * Optional maximum height for the chart container.
   * Accepts CSS values like '40rem' or 500.
   */
  maximumHeight?: number | string;
}

/**
 * A card component that displays a stacked bar chart of customer asset functional status.
 *
 * Features:
 * - Stacked bar visualization of functional vs non-functional assets
 * - Color-coded bars (green for functional, red for non-functional)
 * - Automatic data loading and sorting
 * - Loading state handling
 * - Empty state messaging
 * - Responsive design
 *
 * Visual Elements:
 * - Title showing "Asset status by customer"
 * - Stacked bars for each customer
 * - Color-coded segments for functional status
 * - Customer names as labels
 * - Loading/empty states when applicable
 *
 * @example
 * ```tsx
 * <CustomerFunctionalStatusReportCard
 *   sort={{ property: 'count', direction: 'desc' }}
 * />
 * ```
 */
export function CustomerFunctionalStatusReportCard(props: Props) {
  // Access theme for consistent colors
  const theme = useTheme();
  const { functionalStatusData, isLoading } = useCustomerReport(props.sort);

  // Define colors for functional status (green=functional, red=non-functional)
  const colors = [
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
  ];

  // Determine empty state message based on loading status
  const emptyState = isLoading
    ? 'Loading'
    : 'There is no customer data to display';

  return (
    <BarChartCard
      title="Asset status by customer"
      emptyState={emptyState}
      keys={KEYS}
      data={functionalStatusData}
      colors={colors}
      getLabelForKey={getLabelForKey}
      getLabelForDatum={getLabelForDatum}
      pluralizeTooltipOptions={{ singular: 'asset' }}
      maximumHeight={props.maximumHeight}
    />
  );
}
